import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Modal } from '../../ui/Modal';
import { Icon } from '../../ui/Icon';
import { FetchWizard } from './FetchWizard';

interface ProviderItem {
  id: string;
  slug: string;
  tipo: string;
  categorias_aceitas: string[];
  config_json: any;
  ordem_prioridade: number;
  ativo: boolean;
  criado_em: string;
}

const TIPO_COLOR: Record<string, 'success' | 'info' | 'violet' | 'warning' | 'neutral'> = {
  rank_local: 'success',
  rank_text: 'violet',
  fetch: 'info',
  vision: 'violet',
  extract_text: 'info',
  generate_image: 'warning',
  generate_video: 'warning',
  generate_audio: 'warning',
  transcribe: 'info',
  filter: 'violet'
};

const FILE_TYPES = ['image', 'video', 'audio', 'document', 'presentation', 'spreadsheet', 'data', 'other'];

const editLabel: CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, display: 'block' };
const editInput: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  background: 'var(--bg-0)',
  color: 'var(--text-0)',
  outline: 'none'
};

const miniBtn: CSSProperties = {
  width: 22,
  height: 16,
  fontSize: 11,
  lineHeight: 1,
  padding: 0,
  borderRadius: 4,
  border: '1px solid var(--border)',
  background: 'var(--bg-0)',
  color: 'var(--text-2)',
  cursor: 'pointer'
};

export function Providers() {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<ProviderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProviderItem | null>(null);
  const [wizard, setWizard] = useState(false);
  const [editFetch, setEditFetch] = useState<ProviderItem | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState('');
  const [editOrdem, setEditOrdem] = useState(100);
  const [editCats, setEditCats] = useState<string[]>([]);
  const [editErrors, setEditErrors] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  function openEdit(p: ProviderItem) {
    setDetail(p);
    setEditConfig(JSON.stringify(p.config_json, null, 2));
    setEditOrdem(p.ordem_prioridade);
    setEditCats(p.categorias_aceitas);
    setEditErrors([]);
  }

  async function saveEdit() {
    if (!detail) return;
    let cfg: unknown;
    try {
      cfg = JSON.parse(editConfig);
    } catch {
      setEditErrors(['config_json não é JSON válido']);
      return;
    }
    setEditSaving(true);
    setEditErrors([]);
    const v = await apiFetch<{ ok: boolean; errors: string[] }>('/v1/admin/providers/validate-schema', {
      method: 'POST',
      body: { tipo: detail.tipo, config_json: cfg }
    });
    if (v.ok && v.data && !v.data.ok) {
      setEditSaving(false);
      setEditErrors(v.data.errors);
      return;
    }
    const r = await apiFetch(`/v1/admin/providers/${detail.id}`, {
      method: 'PATCH',
      body: { ordem_prioridade: editOrdem, categorias_aceitas: editCats, config_json: cfg }
    });
    setEditSaving(false);
    if (!r.ok) {
      setEditErrors([r.error?.message ?? 'Falha ao salvar']);
      return;
    }
    toast('Provider atualizado', 'success');
    setDetail(null);
    load();
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await apiFetch<{ items: ProviderItem[] }>('/v1/admin/providers/');
    setLoading(false);
    if (!r.ok || !r.data) {
      setError(r.error?.message ?? 'Falha ao carregar');
      return;
    }
    setItems(r.data.items);
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleAtivo(p: ProviderItem) {
    if (busy) return;
    setBusy(p.id);
    const r = await apiFetch(`/v1/admin/providers/${p.id}`, {
      method: 'PATCH',
      body: { ativo: !p.ativo }
    });
    setBusy(null);
    if (!r.ok) {
      toast(r.error?.message ?? 'Falha', 'danger');
      return;
    }
    toast(`Provider ${!p.ativo ? 'ativado' : 'desativado'}`, 'success');
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, ativo: !p.ativo } : x)));
  }

  function sortedIds(tipo: string): string[] {
    return (byTipo[tipo] ?? [])
      .slice()
      .sort((a, b) => a.ordem_prioridade - b.ordem_prioridade)
      .map((p) => p.id);
  }

  async function applyOrder(tipo: string, ids: string[]) {
    // Otimista: reatribui ordem localmente.
    setItems((prev) =>
      prev.map((p) => {
        const idx = ids.indexOf(p.id);
        return p.tipo === tipo && idx >= 0 ? { ...p, ordem_prioridade: (idx + 1) * 10 } : p;
      })
    );
    const r = await apiFetch('/v1/admin/providers/order', { method: 'PATCH', body: { order: ids } });
    if (!r.ok) {
      toast('Falha ao reordenar', 'danger');
      load();
    } else {
      toast('Ordem atualizada', 'success');
    }
  }

  function reorder(tipo: string, draggedId: string, targetId: string) {
    if (draggedId === targetId) return;
    const ids = sortedIds(tipo);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, draggedId);
    applyOrder(tipo, ids);
  }

  function moveDir(tipo: string, id: string, dir: 'up' | 'down') {
    const ids = sortedIds(tipo);
    const i = ids.indexOf(id);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    applyOrder(tipo, ids);
  }

  // Agrupa por tipo
  const byTipo = items.reduce<Record<string, ProviderItem[]>>((acc, p) => {
    acc[p.tipo] = acc[p.tipo] ?? [];
    acc[p.tipo]!.push(p);
    return acc;
  }, {});
  const tipos = Object.keys(byTipo).sort();

  return (
    <>
      <PageHead
        title="Providers"
        desc="Cadastro de fontes externas e geradores. Ativos participam dos pipelines."
        actions={
          <>
            <Button variant="ghost" icon="refresh" size="sm" onClick={load}>
              Atualizar
            </Button>
            <Button variant="brand" icon="plus" size="sm" onClick={() => setWizard(true)} className="ml-1">
              Novo (Fetch)
            </Button>
          </>
        }
      />

      {wizard && <FetchWizard onClose={() => setWizard(false)} onCreated={load} />}
      {editFetch && (
        <FetchWizard existing={editFetch} onClose={() => setEditFetch(null)} onCreated={load} />
      )}

      {error && (
        <div
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 14px',
            fontSize: 13.5,
            marginBottom: 14
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 56 }} />
          ))}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="grid-bg"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '64px 24px',
            borderRadius: 'var(--r-lg)',
            border: '1px dashed var(--border)',
            gap: 12,
            minHeight: 280,
            justifyContent: 'center'
          }}
        >
          <h3 style={{ fontSize: 15.5 }}>Nenhum provider cadastrado</h3>
          <p style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
            Use seed:providers ou cadastre via API (RN10).
          </p>
        </div>
      )}

      {!loading &&
        !error &&
        tipos.map((tipo) => (
          <div key={tipo} style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 8
              }}
            >
              <Badge color={TIPO_COLOR[tipo] ?? 'neutral'} mono size="sm">
                {tipo}
              </Badge>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                {byTipo[tipo]!.length} provider(s)
              </span>
            </div>
            <div
              style={{
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-lg)',
                overflow: 'hidden'
              }}
            >
              {byTipo[tipo]!
                .slice()
                .sort((a, b) => a.ordem_prioridade - b.ordem_prioridade)
                .map((p, idx) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={() => setDragId(p.id)}
                  onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragId) reorder(tipo, dragId, p.id);
                    setDragId(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderTop: idx === 0 ? 'none' : '1px solid var(--border-soft)',
                    opacity: busy === p.id ? 0.5 : dragId === p.id ? 0.4 : 1,
                    background: dragId && dragId !== p.id ? 'var(--bg-2)' : undefined,
                    transition: 'opacity 0.18s var(--ease)'
                  }}
                >
                  <span
                    title="Arraste para reordenar"
                    style={{ cursor: 'grab', color: 'var(--text-3)', fontSize: 15, lineHeight: 1, userSelect: 'none' }}
                  >
                    ⠿
                  </span>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 'var(--r-sm)',
                      background: 'var(--bg-2)',
                      border: '1px solid var(--border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-2)'
                    }}
                  >
                    <Icon name="plug" size={16} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="mono"
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: 'var(--text-0)'
                      }}
                    >
                      {p.slug}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                      ordem #{p.ordem_prioridade} · {p.categorias_aceitas.join(', ')}
                    </div>
                  </div>
                  {p.ativo ? (
                    <Badge color="success" dot size="sm">
                      ativo
                    </Badge>
                  ) : (
                    <Badge color="neutral" dot size="sm">
                      inativo
                    </Badge>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <button
                      onClick={() => moveDir(tipo, p.id, 'up')}
                      disabled={idx === 0}
                      title="Mover para cima (roda antes)"
                      style={{ ...miniBtn, opacity: idx === 0 ? 0.3 : 1 }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveDir(tipo, p.id, 'down')}
                      disabled={idx === byTipo[tipo]!.length - 1}
                      title="Mover para baixo"
                      style={{ ...miniBtn, opacity: idx === byTipo[tipo]!.length - 1 ? 0.3 : 1 }}
                    >
                      ↓
                    </button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => (p.tipo === 'fetch' ? setEditFetch(p) : openEdit(p))}
                  >
                    Editar
                  </Button>
                  <Button
                    variant={p.ativo ? 'outline' : 'brand'}
                    size="sm"
                    onClick={() => toggleAtivo(p)}
                    disabled={busy === p.id}
                  >
                    {p.ativo ? 'Desativar' : 'Ativar'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}

      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `Editar provider · ${detail.slug}` : ''}
        subtitle={detail ? `${detail.tipo}` : ''}
        width={640}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDetail(null)}>
              Cancelar
            </Button>
            <Button variant="brand" onClick={saveEdit} disabled={editSaving}>
              {editSaving ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ width: 130 }}>
              <label style={editLabel}>Ordem (menor = antes)</label>
              <input
                type="number"
                value={editOrdem}
                onChange={(e) => setEditOrdem(Number(e.target.value))}
                className="focusable"
                style={editInput}
              />
            </div>

            <div>
              <label style={editLabel}>Categorias aceitas</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {FILE_TYPES.map((t) => {
                  const on = editCats.includes(t);
                  return (
                    <button
                      key={t}
                      onClick={() => setEditCats((c) => (on ? c.filter((x) => x !== t) : [...c, t]))}
                      style={{
                        padding: '4px 10px',
                        fontSize: 12,
                        borderRadius: 999,
                        cursor: 'pointer',
                        border: '1px solid var(--border)',
                        background: on ? 'var(--brand-tint)' : 'var(--bg-0)',
                        color: on ? 'var(--brand-strong)' : 'var(--text-2)'
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={editLabel}>config_json</label>
              <textarea
                value={editConfig}
                onChange={(e) => setEditConfig(e.target.value)}
                spellCheck={false}
                rows={16}
                className="focusable"
                style={{
                  ...editInput,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  whiteSpace: 'pre',
                  overflowWrap: 'normal',
                  overflowX: 'auto'
                }}
              />
              <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4 }}>
                Validado pelo schema antes de salvar. Para provider fetch, prefira o assistente "Novo (Fetch)".
              </p>
            </div>

            {editErrors.length > 0 && (
              <div
                style={{
                  background: 'var(--danger-bg)',
                  color: 'var(--danger)',
                  border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
                  borderRadius: 'var(--r-sm)',
                  padding: '10px 12px',
                  fontSize: 12.5
                }}
              >
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {editErrors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
